// =============================================================================
// PPIProjecao — a tela que o aplicador projeta na sala (relógio + QR único)
// =============================================================================
// Rota /ppi-projecao/:ids — onde `ids` é UM processo ("1") ou a SALA, vários
// processos separados por hífen ("1-2"). Fundo branco, relógio digital grande
// (estilo digitalclock.live) e o QR ÚNICO: o candidato escaneia, informa o CPF e
// é direcionado para a prova da trilha dele — não importa de qual processo ele é.
//
// A SALA (2026-08-24): dois processos aplicados no mesmo dia davam dois QRs no
// telão, e dois QRs obrigam o aplicador a explicar de viva voz quem escaneia qual.
// Agora é um só. A regra mora em server/treinamento/ppi/ppi-sala.js.
//
// O botão "Encerrar prova" torna a prova indisponível e marca como entregue quem
// estava presente e não enviou — aproveitando o que foi autosalvo (parcial). Ele
// encerra a SALA INTEIRA: deixar um processo aberto por engano manteria a prova
// acessível para parte das pessoas depois que a sala acabou.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { QrCode, XCircle, Lock, AlertTriangle } from 'lucide-react';

const API = '/api/treinamento/ppi/prova';
const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
});

// HH:MM:SS com dois dígitos (tabular no CSS). Relógio local do aplicador — é só
// referência visual da sala, não trava a prova.
const doisDig = (n) => String(n).padStart(2, '0');

function PPIProjecao() {
    // O parâmetro chega como veio da URL ("1" ou "1-2") e é repassado cru ao
    // backend, que é quem sabe dividi-lo (fonte única da regra).
    const { id: ids } = useParams();
    const [info, setInfo] = useState(null);   // { processos[], tokenQr, encerradaEm, duplicados[] }
    const [erro, setErro] = useState('');
    const [agora, setAgora] = useState(new Date());
    const [encerrando, setEncerrando] = useState(false);

    // Relógio: 1 tick por segundo.
    useEffect(() => {
        const t = setInterval(() => setAgora(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    const carregar = useCallback(async () => {
        try {
            const r = await fetch(`${API}/processo/${ids}/projecao`, { headers: authHeaders() });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message || 'Erro ao carregar a projeção.');
            setInfo(d);
        } catch (e) { setErro(e.message); }
    }, [ids]);
    useEffect(() => { carregar(); }, [carregar]);

    const processos = info?.processos || [];
    const nomes = processos.map((p) => p.nome).join(' · ');
    // Um processo já encerrado numa sala de dois: o QR continua na tela (a outra
    // turma ainda está fazendo prova), mas o aplicador precisa ver isso.
    const jaEncerrados = processos.filter((p) => p.encerradaEm);
    // Conferência de véspera: vaga sem prova publicada vinculada não abre para ninguém.
    const semProva = processos.filter((p) => p.vagasSemProva > 0);

    const encerrar = async () => {
        if (!window.confirm(
            `Encerrar a prova ${processos.length > 1 ? `dos ${processos.length} processos desta sala` : 'deste processo'}?\n\n`
            + '• A prova fica INDISPONÍVEL para todos.\n'
            + '• Quem está presente e não entregou é registrado com as respostas até aqui.\n\n'
            + 'Não dá para reabrir.'
        )) return;
        setEncerrando(true); setErro('');
        try {
            const r = await fetch(`${API}/processo/${ids}/encerrar`, { method: 'POST', headers: authHeaders() });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message || 'Erro ao encerrar.');
            await carregar();
            window.alert(d.message);
        } catch (e) { setErro(e.message); }
        finally { setEncerrando(false); }
    };

    const hora = `${doisDig(agora.getHours())}:${doisDig(agora.getMinutes())}:${doisDig(agora.getSeconds())}`;
    const dataExtenso = agora.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

    return (
        <div style={pagina}>
            {/* Relógio digital grande — o centro da projeção */}
            <div style={{ textAlign: 'center' }}>
                <div style={relogio}>{hora}</div>
                <div style={dataLinha}>{dataExtenso}</div>
            </div>

            {erro && <p style={{ color: '#be123c', fontSize: '15px' }}>{erro}</p>}

            {info && (info.encerradaEm ? (
                // Estado ENCERRADO — sem QR (a prova não abre mais). Só aparece quando
                // TODOS os processos da sala foram encerrados.
                <div style={cartao}>
                    <Lock size={40} color="#b45309" />
                    <h2 style={{ color: '#0f172a', margin: '10px 0 4px 0', fontSize: '22px' }}>Prova encerrada</h2>
                    <p style={{ color: '#64748b', margin: 0, fontSize: '15px' }}>
                        {nomes} · encerrada em {info.encerradaEm}
                    </p>
                </div>
            ) : (
                <div style={cartao}>
                    <p style={{ fontSize: '13px', fontWeight: 800, textTransform: 'uppercase', color: '#0284c7', letterSpacing: '.5px', margin: 0 }}>
                        {nomes}
                    </p>
                    <img
                        src={`${API}/public/qr-proc/${info.tokenQr}`}
                        alt="QR da prova"
                        style={{ width: '300px', height: '300px', maxWidth: '70vw', maxHeight: '70vw' }}
                    />
                    <p style={{ fontSize: '18px', color: '#0f172a', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <QrCode size={20} color="#0284c7" /> Escaneie e informe o seu CPF
                    </p>
                    <p style={{ fontSize: '14px', color: '#64748b', margin: 0, maxWidth: '420px', lineHeight: 1.5 }}>
                        A prova abre para quem foi aprovado na triagem e está com presença marcada. Cada um é
                        direcionado automaticamente para a prova da sua vaga
                        {processos.length > 1 && ', em qualquer um dos processos desta sala'}.
                    </p>

                    {/* Um processo da sala já encerrou e o outro não. O QR segue válido
                        para quem sobrou — mas quem está no telão precisa saber. */}
                    {jaEncerrados.length > 0 && (
                        <p style={{ fontSize: '13px', color: '#b45309', margin: 0, maxWidth: '440px' }}>
                            <strong>{jaEncerrados.map((p) => p.nome).join(', ')}</strong>
                            {jaEncerrados.length > 1 ? ' já foram encerrados' : ' já foi encerrado'} —
                            o QR vale para os demais processos da sala.
                        </p>
                    )}

                    {/* Vaga sem prova publicada: o QR abre e a pessoa leva um "procure
                        o RH" depois de digitar o CPF — com a sala cheia. O vínculo é
                        um clique na aba Aplicação, e dá para fazer com o telão ligado. */}
                    {semProva.length > 0 && (
                        <div style={avisoDuplicado}>
                            <AlertTriangle size={18} color="#b45309" style={{ flexShrink: 0, marginTop: '1px' }} />
                            <div>
                                <strong>
                                    {semProva.reduce((n, p) => n + p.vagasSemProva, 0)} vaga(s) sem prova publicada
                                    vinculada{semProva.length > 1 || processos.length > 1 ? ` em ${semProva.map((p) => p.nome).join(', ')}` : ''}.
                                </strong>{' '}
                                Ninguém dessas vagas consegue abrir a prova. Vincule em{' '}
                                <strong>Aplicação &amp; Ranking → Vagas &amp; provas do processo</strong> antes de chamar a sala.
                            </div>
                        </div>
                    )}

                    {/* O CPF em dois processos da mesma sala não tem desempate honesto:
                        o servidor manda procurar o RH em vez de abrir a prova errada.
                        Aqui o aplicador vê ANTES, com o nome, e resolve fora da fila. */}
                    {info.duplicados?.length > 0 && (
                        <div style={avisoDuplicado}>
                            <AlertTriangle size={18} color="#b45309" style={{ flexShrink: 0, marginTop: '1px' }} />
                            <div>
                                <strong>{info.duplicados.length} pessoa(s) inscrita(s) em mais de um processo desta sala.</strong>{' '}
                                A prova <strong>não abre</strong> para elas — o sistema não escolhe por conta própria qual
                                trilha aplicar. Resolva a inscrição duplicada antes de chamar a sala:{' '}
                                {info.duplicados.map((d) => d.nome).join(', ')}.
                            </div>
                        </div>
                    )}

                    <button onClick={encerrar} disabled={encerrando} style={btnEncerrar}>
                        <XCircle size={18} /> {encerrando ? 'Encerrando…' : 'Encerrar prova'}
                    </button>
                </div>
            ))}
        </div>
    );
}

const pagina = {
    backgroundColor: '#fff', minHeight: '100vh', width: '100%',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: '32px', padding: '40px 16px', boxSizing: 'border-box',
    fontFamily: 'system-ui, -apple-system, sans-serif',
};
// Relógio no estilo digitalclock.live: enorme, monoespaçado, tabular.
const relogio = {
    fontSize: 'clamp(64px, 18vw, 200px)', fontWeight: 800, color: '#0f172a',
    lineHeight: 1, fontVariantNumeric: 'tabular-nums',
    fontFamily: '"SF Mono", "Roboto Mono", Menlo, Consolas, monospace', letterSpacing: '2px',
};
const dataLinha = {
    fontSize: 'clamp(14px, 2.5vw, 22px)', color: '#64748b', marginTop: '8px', textTransform: 'capitalize',
};
const cartao = {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', textAlign: 'center',
};
const avisoDuplicado = {
    display: 'flex', gap: '10px', alignItems: 'flex-start', textAlign: 'left',
    backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px',
    padding: '12px 14px', maxWidth: '460px', fontSize: '13px', color: '#92400e', lineHeight: 1.5,
};
const btnEncerrar = {
    display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '8px',
    padding: '11px 20px', borderRadius: '10px', border: '1px solid #fecaca',
    backgroundColor: '#fff', color: '#b91c1c', fontSize: '15px', fontWeight: 700, cursor: 'pointer',
};

export default PPIProjecao;
